/**
 * Notches Dashboard - preview build (post 2026-05-17).
 *
 * Gated by entitlement `notches.preview`. Net-new surface; nothing in the
 * existing system is modified or touched.
 *
 * Design goals (from Bobby 2026-05-17):
 *   1. Easier to navigate - one-click into each service the user has.
 *      Cards = direct deeplinks, not landing pages with sub-clicks.
 *   2. Tease unsubscribed services - show locked tiles with a value-prop
 *      blurb + Learn more CTA so users see what's available beyond what
 *      they currently own.
 *
 * Layout:
 *   - "Your services" row: tiles for what the user has (active gradient,
 *     primary deeplink).
 *   - "Explore" row: tiles for what the user does NOT have (dimmed, lock
 *     glyph, teaser blurb, Learn more CTA).
 *
 * Reads ONLY user.entitlements via hasSubscription. No legacy flag fallback.
 */
import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { Navigate, useNavigate } from 'react-router-dom';
import { hasSubscription } from '../../lib/entitlements';
import { ENTITLEMENTS, EntitlementKey } from '../../constants/entitlements';
import { RiskDisclaimer } from '../shared/RiskDisclaimer';

const c = {
  bg: '#0f172a',
  card: '#1f2937',
  cardAlt: '#1a2332',
  border: '#374151',
  text: '#f9fafb',
  muted: '#9ca3af',
  dim: '#6b7280',
  green: '#10b981',
  greenBg: '#10b98115',
  amber: '#f59e0b',
  amberBg: '#f59e0b15',
  blue: '#3b82f6',
  blueBg: '#3b82f615',
  purple: '#8b5cf6',
  purpleBg: '#8b5cf615',
};

// One tile = one product / service. `to` is where clicking the tile takes
// the user (deeplink straight into the working surface, not a landing page).
// `entitlement` is the gate; `teaser` is the value-prop shown when locked.
// `subActions` surface the legacy sub-views (Trades/Analytics/Audit/etc) so
// users can jump straight to them without a second click through the product
// landing page. Same product, different facet.
interface SubAction {
  label: string;
  to: string;
}
interface ServiceTile {
  // Gate: ANY of these entitlements unlocks the tile. For per-symbol bundles
  // (e.g. Index Options covers SPX + SPY), supply both keys. For single
  // products, supply one. Empty array + alwaysActive=true = free product.
  keys: EntitlementKey[];
  label: string;
  blurb: string;        // short pitch shown on every tile
  teaser: string;       // longer "why you want this" shown on LOCKED tiles only
  to: string;           // route to deeplink to when unlocked
  subActions?: SubAction[];
  alwaysActive?: boolean;  // free products - never gated, always rendered as active
  pillar: 'signals' | 'markets' | 'reports' | 'auto-traders';
  accent: string;       // pillar color
}

const TILES: ServiceTile[] = [
  // --- Signals pillar
  {
    // Per-symbol bundle - tile renders active if user has SPX OR SPY entitlement.
    keys: [ENTITLEMENTS.SIGNALS_OPTIONS_SPX, ENTITLEMENTS.SIGNALS_OPTIONS_SPY],
    label: 'Options Research - Index',
    blurb: 'SPX & SPY candidate picks, scored across all DTEs.',
    teaser: 'Daily-graded options trades on SPX & SPY with AI-narrated reasoning + skew/macro context. Each symbol is a separate purchase.',
    to: '/notches?group=index',
    pillar: 'signals',
    accent: '#10b981',
  },
  {
    keys: [ENTITLEMENTS.SIGNALS_OPTIONS_ES, ENTITLEMENTS.SIGNALS_OPTIONS_MES],
    label: 'Options Research - Futures',
    blurb: 'ES & MES candidate picks for futures-options sellers.',
    teaser: 'Futures-options income with 23-hour markets; same scoring model as Index Options. Each symbol is a separate purchase.',
    to: '/notches?group=futures',
    pillar: 'signals',
    accent: '#10b981',
  },
  {
    keys: [ENTITLEMENTS.SIGNALS_OPTIONS_EQUITY],
    label: 'Options Research - Equity',
    blurb: 'Single-stock options picks on liquid US equities.',
    teaser: 'Per-name options-income candidates on AAPL, NVDA, TSLA and other liquid US equities.',
    to: '/notches/signals/options/equity',
    pillar: 'signals',
    accent: '#10b981',
  },
  {
    keys: [ENTITLEMENTS.SIGNALS_STOCKS],
    label: 'Stock Swing Research',
    blurb: 'Multi-day swing setups on liquid US equities.',
    teaser: 'Multi-factor swing trades emailed daily; verified win-rate banner; clear entries + exits.',
    to: '/notches/signals/stocks',
    pillar: 'signals',
    accent: '#10b981',
  },
  // --- Markets pillar
  {
    keys: [ENTITLEMENTS.MARKETS],
    label: 'Market Commentary',
    blurb: 'Daily morning research + voice notes from the WFS desk.',
    teaser: 'Plain-English daily reads on what moved + what to watch; not for the screen-watchers.',
    to: '/markets',
    pillar: 'markets',
    accent: '#3b82f6',
  },
  // --- Auto-traders pillar
  {
    keys: [ENTITLEMENTS.AUTO_TRADERS_TASTYTRADE],
    label: 'TastyTrade Auto-Trader',
    blurb: 'Hands-off options-income engines on your TT account.',
    teaser: 'Configure preset + risk; the engine opens / monitors / closes spreads on your TT account.',
    to: '/notches/auto-traders/tastytrade',
    subActions: [
      { label: 'Trades', to: '/notches/auto-traders/tastytrade/trades' },
      { label: 'Analytics', to: '/notches/auto-traders/tastytrade/analytics' },
      { label: 'Audit', to: '/notches/auto-traders/tastytrade/audit' },
    ],
    pillar: 'auto-traders',
    accent: '#8b5cf6',
  },
  {
    keys: [ENTITLEMENTS.AUTO_TRADERS_ALPACA],
    label: 'Alpaca Auto-Trader',
    blurb: 'Stock-research-driven trades on your Alpaca account.',
    teaser: 'Wakes on every stock pick; sizes per your risk; fully audit-logged.',
    to: '/notches/auto-traders/alpaca',
    subActions: [
      { label: 'Signals', to: '/notches/auto-traders/alpaca/signals' },
      { label: 'Trades', to: '/notches/auto-traders/alpaca/trades' },
      { label: 'Portfolio', to: '/notches/auto-traders/alpaca/portfolio' },
      { label: 'Audit', to: '/notches/auto-traders/alpaca/audit' },
    ],
    pillar: 'auto-traders',
    accent: '#8b5cf6',
  },
];

const PILLAR_LABELS = {
  signals:       { label: 'Research',      color: '#10b981' },
  markets:       { label: 'Markets',       color: '#3b82f6' },
  reports:       { label: 'Reports',       color: '#f59e0b' },
  'auto-traders':{ label: 'Auto-Traders',  color: '#8b5cf6' },
};

export const NotchesDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Hard gate. Users without notches.preview bounce to the existing surface.
  if (!hasSubscription(user, ENTITLEMENTS.NOTCHES_PREVIEW)) {
    return <Navigate to="/notches" replace />;
  }

  const renderActiveTile = (t: ServiceTile) => (
    // div + role=button instead of <button> so the sub-action chips below can
    // be their own interactive elements (nesting interactive content inside a
    // <button> is invalid HTML and browsers flatten it).
    <div
      key={t.label}
      role="button"
      tabIndex={0}
      onClick={() => navigate(t.to)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault(); navigate(t.to);
        }
      }}
      style={{
        background: c.card,
        border: `1px solid ${c.border}`,
        borderLeft: `3px solid ${t.accent}`,
        borderRadius: 8,
        padding: '14px 16px',
        cursor: 'pointer',
        textAlign: 'left',
        color: c.text,
        transition: 'background 0.1s',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = c.cardAlt; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = c.card; }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: 15, fontWeight: 600 }}>{t.label}</span>
        <span style={{ fontSize: 10, color: t.accent, fontWeight: 700, letterSpacing: 0.5 }}>
          {PILLAR_LABELS[t.pillar].label.toUpperCase()}
        </span>
      </div>
      <div style={{ fontSize: 12, color: c.muted, lineHeight: 1.4 }}>{t.blurb}</div>
      <div style={{ fontSize: 11, color: t.accent, marginTop: 4 }}>Open &rarr;</div>
      {t.subActions && t.subActions.length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8,
          paddingTop: 8, borderTop: `1px dashed ${c.border}`,
        }}>
          {t.subActions.map((a) => (
            <span
              key={a.to}
              role="link"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); navigate(a.to); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault(); e.stopPropagation(); navigate(a.to);
                }
              }}
              style={{
                fontSize: 11, color: c.muted, padding: '2px 8px',
                border: `1px solid ${c.border}`, borderRadius: 10,
                background: 'transparent', cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.color = t.accent;
                (e.currentTarget as HTMLElement).style.borderColor = t.accent;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.color = c.muted;
                (e.currentTarget as HTMLElement).style.borderColor = c.border;
              }}
            >{a.label}</span>
          ))}
        </div>
      )}
    </div>
  );

  const renderLockedTile = (t: ServiceTile) => (
    <div
      key={t.label}
      style={{
        background: c.cardAlt,
        border: `1px dashed ${c.border}`,
        borderLeft: `3px solid ${c.dim}`,
        borderRadius: 8,
        padding: '14px 16px',
        opacity: 0.85,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: c.muted }}>
          <span style={{ marginRight: 6, opacity: 0.6 }}>&#x1F512;</span>
          {t.label}
        </span>
        <span style={{ fontSize: 10, color: c.dim, fontWeight: 700, letterSpacing: 0.5 }}>
          {PILLAR_LABELS[t.pillar].label.toUpperCase()}
        </span>
      </div>
      <div style={{ fontSize: 12, color: c.muted, lineHeight: 1.4 }}>{t.teaser}</div>
      <button
        onClick={() => navigate('/upgrade')}
        style={{
          marginTop: 6,
          alignSelf: 'flex-start',
          background: 'transparent',
          color: t.accent,
          border: `1px solid ${t.accent}`,
          padding: '3px 10px',
          borderRadius: 4,
          fontSize: 11,
          cursor: 'pointer',
          fontWeight: 600,
        }}
      >
        Add to your plan
      </button>
    </div>
  );

  return (
    <div className="dashboard-container" style={{ background: c.bg, minHeight: '100vh', color: c.text }}>
      <Navigation />
      <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
        <h1 style={{ margin: '0 0 4px', fontSize: 28 }}>Notches</h1>
        <p style={{ color: c.muted, fontSize: 14, marginTop: 0, marginBottom: 28 }}>
          Everything WFS does, in one place. Jump straight to what you own; preview what you do not.
        </p>

        {/* Grouped by pillar so users can scan "what do I have under Signals
            vs Reports vs Auto-Traders" without color-decoding. Each pillar
            section has a header bar in the pillar's accent color; tiles
            inside the section render active first, then locked. */}
        {(['signals', 'markets', 'reports', 'auto-traders'] as const).map((pillar) => {
          const meta = PILLAR_LABELS[pillar];
          const pillarTiles = TILES.filter((t) => t.pillar === pillar);
          if (pillarTiles.length === 0) return null;
          // alwaysActive tiles (free products like IV Reports + Daily Report)
          // bypass the gate. Otherwise: tile is active if user has ANY of its
          // listed entitlements (per-symbol bundles like Index Options check
          // both SPX and SPY).
          const tileHasAccess = (t: ServiceTile) =>
            t.alwaysActive || t.keys.some((k) => hasSubscription(user, k));
          const active = pillarTiles.filter(tileHasAccess);
          const locked = pillarTiles.filter((t) => !tileHasAccess(t));

          return (
            <section key={pillar} style={{ marginBottom: 32 }}>
              <button
                onClick={() => navigate(`/notches/dashboard/${pillar}`)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                  marginBottom: 12, paddingBottom: 8,
                  borderBottom: `2px solid ${meta.color}`,
                  background: 'none', border: 'none', borderRadius: 0,
                  cursor: 'pointer', textAlign: 'left', color: c.text,
                }}
                title={`Open ${meta.label} pillar`}
              >
                <span style={{
                  width: 8, height: 24, borderRadius: 2, background: meta.color,
                }} />
                <h2 style={{
                  margin: 0, fontSize: 18, fontWeight: 700, color: c.text,
                }}>{meta.label}</h2>
                <span style={{ fontSize: 11, color: c.muted }}>
                  {active.length} active &middot; {locked.length} available
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 12, color: meta.color, fontWeight: 600 }}>
                  Open &rarr;
                </span>
              </button>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 12,
              }}>
                {active.map(renderActiveTile)}
                {locked.map(renderLockedTile)}
              </div>
            </section>
          );
        })}

        <div style={{ marginTop: 24, fontSize: 11, color: c.dim }}>
          Signed in as <strong style={{ color: c.muted }}>{user?.email}</strong> &middot;
          {' '}<strong style={{ color: c.muted }}>{user?.entitlements?.length ?? 0}</strong> entitlements
        </div>
      </div>
      <RiskDisclaimer />
    </div>
  );
};

export default NotchesDashboard;
